import { ComponentType, lazy, LazyExoticComponent } from 'react';

export interface LoadableComponent<T extends ComponentType<any>>
  extends LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
  load: () => Promise<{ default: T }>;
}

/**
 * Drop-in replacement for @loadable/component that works with Vite.
 * This wrapper makes React.lazy compatible with existing loadable() calls.
 *
 * @param loader - Dynamic import function that returns a Promise of the component module
 * @returns A lazy-loaded component with preload methods attached
 */
export default function loadable<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
): LoadableComponent<T> {
  const LazyComponent = lazy(loader) as LoadableComponent<T>;

  // Add preload() and load() methods for compatibility with any existing calls
  // These allow you to imperatively trigger the loading of a component
  LazyComponent.preload = loader;
  LazyComponent.load = loader;

  return LazyComponent;
}
